/* =========================================================
   DEFY THE NORM / Cart (drawer + page)
   - Re-renders itself with the Section Rendering API after every change
   - Listens to Emerge's add-to-cart events so product pages still open the drawer
   - Upgrades the upsell rail with Shopify recommendations for the first cart item
   ========================================================= */
let Theme = null;
import('@troop-themes/main').then((m) => { Theme = m; }).catch(() => {});

const R = () => (window.Shopify && window.Shopify.routes) || {};
const url = (key, fallback) => R()[key] || fallback;
const recCache = new Map();

const fire = (name, detail) => window.dispatchEvent(new CustomEvent(name, { detail }));

class DtnCart extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.view = this.dataset.view;
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('[data-code-input]')) { e.preventDefault(); this.applyCode(); }
    });
    this.addEventListener('input', (e) => {
      if (e.target.matches('[data-cart-note]')) this.saveNote(e.target.value);
    });
    this.addEventListener('submit', (e) => {
      const btn = e.target.querySelector('[data-checkout]');
      if (btn) btn.setAttribute('aria-busy', 'true');
    });
    window.addEventListener('pageshow', () => this.querySelectorAll('[aria-busy]').forEach((b) => b.removeAttribute('aria-busy')));

    /* Emerge adds (product page, quick add, kit builder) send back the cart-drawer section */
    window.addEventListener('theme:cart:drawer:updateHtml', (e) => {
      const html = e.detail && e.detail.section;
      if (this.view === 'drawer' && html) {
        this.swap(html);
        fire('theme:cart:updated', this);
      } else if (this.view === 'page') {
        this.refresh();
      }
    });
    window.addEventListener('theme:cart:refresh', () => this.refresh());
    this.upgradeRail();
  }

  /* ---------- helpers ---------- */
  carts() { return [...document.querySelectorAll('dtn-cart[data-section-id]')]; }
  sectionIds() { return [...new Set(this.carts().map((c) => c.dataset.sectionId))]; }

  busy(on, el) {
    this.toggleAttribute('aria-busy', on);
    if (el) el.classList.toggle('is-busy', on);
  }

  error(msg) {
    const box = this.querySelector('[data-cart-error]');
    if (!box) return;
    box.textContent = msg || '';
    box.hidden = !msg;
  }

  async post(path, body) {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: this.sectionIds(), sections_url: window.location.pathname })
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.description || json.message || 'Something went wrong. Please try again.');
    return json;
  }

  apply(json) {
    if (json.sections) {
      this.carts().forEach((c) => {
        const html = json.sections[c.dataset.sectionId];
        if (html) c.swap(html);
      });
    }
    fire('theme:cart:updated', this);
    this.syncCount(json);
  }

  async syncCount(json) {
    let cart = json && Array.isArray(json.items) && typeof json.item_count === 'number' ? json : null;
    if (!cart) {
      try { cart = await (await fetch(`${url('cart', '/cart')}.js`)).json(); } catch (e) { return; }
    }
    const C = Theme && Theme.Cart;
    if (C) { C.fillBasket(cart.items); C.updateTotals(cart.item_count); }
    else document.querySelectorAll('[data-cart-count]').forEach((n) => n.setAttribute('data-cart-count', cart.item_count));
  }

  swap(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('dtn-cart');
    if (!next) return;
    const scroll = this.querySelector('[data-cart-scroll]');
    const top = scroll ? scroll.scrollTop : 0;
    const track = this.querySelector('[data-rail] .dtn-rail__track');
    const left = track ? track.scrollLeft : 0;
    const openCode = this.querySelector('.dtn-cart__code-box[open]');
    const focusKey = document.activeElement && this.contains(document.activeElement) ? this.focusKey(document.activeElement) : null;

    /* keep an already upgraded rail while the new one loads */
    const oldRail = this.querySelector('[data-rail][data-upgraded]');

    ['count', 'total', 'ids', 'codes'].forEach((k) => { this.dataset[k] = next.dataset[k] || ''; });
    this.innerHTML = next.innerHTML;

    const ns = this.querySelector('[data-cart-scroll]');
    if (ns) ns.scrollTop = top;
    if (openCode) { const box = this.querySelector('.dtn-cart__code-box'); if (box) box.open = true; }
    const newRail = this.querySelector('[data-rail]');
    if (oldRail && newRail && newRail.dataset.mode === 'smart' && newRail.dataset.productId === oldRail.dataset.productId) {
      const cached = recCache.get(this.recKey(newRail));
      if (cached) this.placeRail(newRail, cached);
    }
    const nt = this.querySelector('[data-rail] .dtn-rail__track');
    if (nt) nt.scrollLeft = left;
    if (focusKey) this.restoreFocus(focusKey);
    if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
      try { window.Shopify.PaymentButton.init(); } catch (e) { /* express buttons not on this page */ }
    }
    this.upgradeRail();
  }

  focusKey(el) {
    const line = el.closest('[data-key]');
    if (line) return { key: line.dataset.key, sel: el.matches('[data-qty="0"]') ? '.dtn-cart__rm' : (el.getAttribute('aria-label') || '').startsWith('Decrease') ? '[data-qty]:first-child' : '.dtn-cart__qty button:last-child' };
    if (el.matches('[data-bump-variant]')) return { sel: '[data-bump-variant]' };
    if (el.matches('[data-code-input]')) return { sel: '[data-code-input]' };
    return { sel: '.dtn-cart__close, .dtn-cart__title' };
  }

  restoreFocus({ key, sel }) {
    let target = null;
    if (key) {
      const line = this.querySelector(`[data-key="${CSS.escape(key)}"]`);
      target = line ? line.querySelector(sel) : null;
    } else target = this.querySelector(sel);
    if (!target || target.disabled) target = this.querySelector('[data-cart-close]') || this.querySelector('[data-checkout]');
    if (target) target.focus({ preventScroll: true });
  }

  /* ---------- actions ---------- */
  onClick(e) {
    const t = e.target.closest('button, a');
    if (!t || !this.contains(t)) return;

    if (t.matches('[data-cart-close]')) { fire('theme:drawer:close'); return; }
    if (t.matches('[data-qty]')) {
      const line = t.closest('[data-key]');
      if (line) this.change(line.dataset.key, parseInt(t.dataset.qty, 10), line);
      return;
    }
    if (t.matches('[data-bump-variant]')) { this.toggleBump(t); return; }
    if (t.matches('[data-add]')) { this.add(t.dataset.add, t); return; }
    if (t.matches('[data-add-picked]')) {
      const sel = t.parentElement.querySelector('[data-add-select]');
      if (sel) this.add(sel.value, t);
      return;
    }
    if (t.matches('[data-code-apply]')) { this.applyCode(); return; }
    if (t.matches('[data-code-remove]')) { this.removeCode(t.dataset.codeRemove); }
  }

  async change(key, quantity, line) {
    this.error('');
    this.busy(true, line);
    try {
      const json = await this.post(`${url('cart_change', '/cart/change')}.js`, { id: key, quantity: Math.max(0, quantity) });
      this.apply(json);
    } catch (err) {
      this.error(err.message);
      this.busy(false, line);
    } finally { this.busy(false); }
  }

  async add(variantId, btn) {
    if (!variantId) return;
    this.error('');
    if (btn) btn.setAttribute('aria-busy', 'true');
    this.busy(true);
    try {
      const json = await this.post(`${url('cart_add', '/cart/add')}.js`, { items: [{ id: Number(variantId), quantity: 1 }] });
      this.apply(json);
    } catch (err) {
      this.error(err.message);
      if (btn) btn.removeAttribute('aria-busy');
    } finally { this.busy(false); }
  }

  toggleBump(btn) {
    if (btn.dataset.bumpKey) this.change(btn.dataset.bumpKey, 0, btn);
    else this.add(btn.dataset.bumpVariant, btn);
  }

  codes() { return (this.dataset.codes || '').split(',').map((c) => c.trim()).filter(Boolean); }

  async applyCode() {
    const input = this.querySelector('[data-code-input]');
    const code = input ? input.value.trim() : '';
    if (!code) { if (input) input.focus(); return; }
    const list = this.codes().filter((c) => c.toLowerCase() !== code.toLowerCase());
    list.push(code);
    this.error('');
    this.busy(true);
    try {
      const json = await this.post(`${url('cart_update', '/cart/update')}.js`, { discount: list.join(',') });
      const hit = (json.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      this.apply(json);
      if (!hit || hit.applicable === false) this.error(`"${code}" can't be used on this cart. It may have expired or need other items.`);
    } catch (err) { this.error(err.message); } finally { this.busy(false); }
  }

  async removeCode(code) {
    this.busy(true);
    try {
      const json = await this.post(`${url('cart_update', '/cart/update')}.js`, { discount: this.codes().filter((c) => c !== code).join(',') });
      this.apply(json);
    } catch (err) { this.error(err.message); } finally { this.busy(false); }
  }

  saveNote(note) {
    clearTimeout(this._note);
    this._note = setTimeout(() => {
      fetch(`${url('cart_update', '/cart/update')}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note })
      }).catch(() => {});
    }, 400);
  }

  async refresh() {
    const id = this.dataset.sectionId;
    try {
      const res = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(id)}`);
      const json = await res.json();
      if (json[id]) this.swap(json[id]);
    } catch (e) { /* keep current markup */ }
  }

  /* ---------- recommendations ---------- */
  recKey(rail) { return `${rail.dataset.productId}|${this.dataset.total}|${this.dataset.ids}`; }

  placeRail(rail, html) {
    rail.outerHTML = html;
    const placed = this.querySelector('[data-rail]');
    if (placed) { placed.dataset.upgraded = ''; placed.dataset.mode = 'smart'; placed.dataset.productId = rail.dataset.productId; }
  }

  async upgradeRail() {
    const rail = this.querySelector('[data-rail][data-mode="smart"]:not([data-upgraded])');
    if (!rail || !rail.dataset.productId) return;
    const key = this.recKey(rail);
    if (recCache.has(key)) { this.placeRail(rail, recCache.get(key)); return; }
    const base = url('product_recommendations', '/recommendations/products');
    try {
      const res = await fetch(`${base}?product_id=${rail.dataset.productId}&limit=10&intent=${rail.dataset.intent || 'related'}&section_id=dtn-cart-recs`);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const got = doc.querySelector('[data-rail]');
      if (!got || parseInt(got.dataset.count || '0', 10) < 2) return;
      recCache.set(key, got.outerHTML);
      if (rail.isConnected && this.recKey(rail) === key) this.placeRail(rail, got.outerHTML);
    } catch (e) { /* fallback list stays */ }
  }
}

if (!customElements.get('dtn-cart')) customElements.define('dtn-cart', DtnCart);
